import { adminTenantId, SignInIdentifier } from '@logto/schemas';
import { HTTPError } from 'ky';
import { useContext, useState } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';

import RegistrationConsentContext from './RegistrationConsentContext';
import { precommitRegistration } from './api';
import {
  clearRegistrationLegalState,
  getRegistrationLegalState,
  setRegistrationLegalState,
} from './session';

export default function useRegistrationConsent(restartReview?: () => void) {
  const { experienceSettings } = useContext(PageContext);
  const requestReview = useContext(RegistrationConsentContext);
  const reviewAgain = restartReview ?? requestReview;
  const [consentError, setConsentError] = useState('');
  const saveRegistrationConsent = async (type: SignInIdentifier, value: string) => {
    setConsentError('');
    // Console administrators must be able to bootstrap Logto before the Relaxi API is configured.
    if (experienceSettings?.tenantId === adminTenantId) {
      return true;
    }
    const state = getRegistrationLegalState();
    const { obligations, accepted = [] } = state;
    if (!state.reviewed || !obligations) {
      reviewAgain();
      return false;
    }
    // No acceptance exists to precommit before the first documents are published.
    // The Relaxi API rechecks the current catalog when creating the local profile.
    if (obligations.requirements.length === 0) {
      return true;
    }
    if (type !== SignInIdentifier.Email && type !== SignInIdentifier.Phone) {
      setConsentError('Для регистрации укажите почту или телефон.');
      return false;
    }
    try {
      const result = await precommitRegistration(
        obligations.registrationSessionId,
        obligations.requirements
          .filter(({ id, displayMode }) => accepted.includes(id) && displayMode !== 'notice_only')
          .map(({ id, version }) => ({ requirementId: id, documentVersionId: version.id })),
        { type, value }
      );
      setRegistrationLegalState({
        ...state,
        obligations: { ...obligations, expiresAt: result.expiresAt },
      });
      return true;
    } catch (error: unknown) {
      if (error instanceof HTTPError) {
        const body: unknown = await error.response.json().catch(() => null);
        if (
          body &&
          typeof body === 'object' &&
          'code' in body &&
          (body.code === 'registration_session_expired' ||
            body.code === 'registration_session_stale')
        ) {
          clearRegistrationLegalState();
          reviewAgain();
          return false;
        }
      }
      setConsentError('Не удалось сохранить согласия. Нажмите «Создать аккаунт» ещё раз.');
      return false;
    }
  };
  return { consentError, saveRegistrationConsent };
}
